import styled from 'styled-components';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;
  padding: 10px;
  overflow: hidden;
`;

export const Card = styled.div`
  background-color: white;
  border-radius: 15px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  padding: 15px;
  width: 100%;
  max-width: 500px;
  max-height: calc(100vh - 50px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  
  /* Adjust padding for smaller screens */
  @media (max-height: 700px) {
    padding: 12px;
    max-height: calc(100vh - 45px);
  }
  
  @media (max-height: 600px) {
    padding: 8px;
    max-height: calc(100vh - 40px);
  }
`;

export const Title = styled.h1`
  color: #333;
  font-size: 24px;
  margin-bottom: 20px;
  text-align: center;
`;

export const Subtitle = styled.h2`
  color: #555;
  font-size: 18px;
  margin-bottom: 15px;
  text-align: center;
`;

export const Button = styled.button`
  background-color: #4a90e2;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin: 10px 0;
  width: 100%;

  &:hover {
    background-color: #3a7bc8;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

export const AnswerButton = styled(Button)`
  background-color: #4a90e2;
  color: white;
  margin: 8px 0;
  text-align: left;
  word-break: break-word;
  
  &[data-answer-state="correct"] {
    background-color: #4caf50;
  }
  
  &[data-answer-state="incorrect"] {
    background-color: #f44336;
  }
  
  &[data-answer-state="selected"] {
    background-color: #ffeb3b;
    color: #333;
  }
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
  
  /* Reduce spacing for smaller screens */
  @media (max-height: 700px) {
    gap: 5px;
  }
  
  @media (max-height: 600px) {
    gap: 3px;
  }
`;

export const Select = styled.select`
  padding: 8px;
  margin-bottom: 10px;
  border-radius: 8px;
  border: 1px solid #ddd;
  font-size: 16px;
  width: 100%;
  
  @media (max-height: 700px) {
    padding: 6px;
    margin-bottom: 8px;
    font-size: 14px;
  }
  
  @media (max-height: 600px) {
    padding: 4px;
    margin-bottom: 6px;
    font-size: 12px;
  }
`;

export const Label = styled.label`
  margin-bottom: 3px;
  font-weight: 600;
  color: #555;
  
  @media (max-height: 600px) {
    margin-bottom: 2px;
  }
`;

export const Input = styled.input`
  padding: 8px;
  margin-bottom: 10px;
  border-radius: 8px;
  border: 1px solid #ddd;
  font-size: 16px;
  width: 100%;
  
  @media (max-height: 700px) {
    padding: 6px;
    margin-bottom: 8px;
    font-size: 14px;
  }
  
  @media (max-height: 600px) {
    padding: 4px;
    margin-bottom: 6px;
    font-size: 12px;
  }
`;

export const Slider = styled.input.attrs({ type: 'range' })`
  -webkit-appearance: none;
  width: 100%;
  height: 12px;
  border-radius: 10px;
  background: #e0e0e0;
  outline: none;
  margin-bottom: 12px;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #4a90e2;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  }
  
  &::-moz-range-thumb {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #4a90e2;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  }
  
  &:active::-webkit-slider-thumb {
    background: #3a7bc8;
  }
  
  &:active::-moz-range-thumb {
    background: #3a7bc8;
  }
  
  @media (max-height: 700px) {
    height: 10px;
    margin-bottom: 10px;
    
    &::-webkit-slider-thumb {
      width: 18px;
      height: 18px;
    }
    
    &::-moz-range-thumb {
      width: 18px;
      height: 18px;
    }
  }
  
  @media (max-height: 600px) {
    height: 8px;
    margin-bottom: 8px;
    
    &::-webkit-slider-thumb {
      width: 16px;
      height: 16px;
    }
    
    &::-moz-range-thumb {
      width: 16px;
      height: 16px;
    }
  }
`;

export const SliderContainer = styled.div`
  width: 100%;
  margin-bottom: 10px;
`;

export const SliderValue = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
  
  span {
    font-weight: 600;
    color: #4a90e2;
    font-size: 18px;
  }
`;

export const QuestionText = styled.p`
  font-size: 18px;
  margin-bottom: 20px;
  line-height: 1.5;
  color: #333;
`;

export const CategoryTag = styled.span`
  background-color: #e0e0e0;
  color: #555;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  margin-bottom: 10px;
  display: inline-block;
`;

export const DifficultyTag = styled.span<{ difficulty: string }>`
  background-color: ${props => 
    props.difficulty === 'easy' ? '#4caf50' : 
    props.difficulty === 'medium' ? '#ff9800' : 
    '#f44336'};
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  margin-bottom: 10px;
  margin-left: 8px;
  display: inline-block;
`;

export const ScoreText = styled.p`
  font-size: 18px;
  font-weight: 600;
  margin: 10px 0;
  color: #4a90e2;
`;

export const LivesContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 15px;
`;

export const Heart = styled.span`
  color: #ddd;
  font-size: 24px;
  margin: 0 5px;
  
  &[data-filled="true"] {
    color: #f44336;
  }
`;

export const LoadingSpinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #4a90e2;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 20px auto;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export const ErrorText = styled.p`
  color: #f44336;
  margin: 10px 0;
  text-align: center;
`;

export const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background-color: #e0e0e0;
  border-radius: 4px;
  margin-bottom: 20px;
  overflow: hidden;
`;

export const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  width: ${props => `${props.progress}%`};
  background-color: #4a90e2;
  transition: width 0.3s ease;
`;

export const GameOverContainer = styled.div`
  text-align: center;
  padding: 20px;
`;

export const FinalScore = styled.h2`
  font-size: 32px;
  color: #4a90e2;
  margin: 20px 0;
`;

export const ResultText = styled.p`
  font-size: 18px;
  margin: 10px 0;
  color: #555;
`;

export const ButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 20px;
  
  ${Button} {
    width: 48%;
  }
`;
